/**
 * Cart Page UI - Handles UI-specific interactions
 * Data operations are handled by js-cart-controller.js
 */

document.addEventListener('DOMContentLoaded', function() {
  // ========================================
  // ACCORDION FUNCTIONALITY
  // ========================================
  // Handled by global js-accordion.js

  // ========================================
  // APPLICATION FIELD SHOW/HIDE
  // ========================================
  const rentalApplication = document.getElementById('rental-application');
  const applicationOtherField = document.getElementById('application-other-field');

  if (rentalApplication && applicationOtherField) {
    function toggleOtherField() {
      const selectedOption = rentalApplication.options[rentalApplication.selectedIndex];
      if (selectedOption && selectedOption.value === 'Other Application') {
        applicationOtherField.style.display = 'block';
      } else {
        applicationOtherField.style.display = 'none';
      }
    }

    rentalApplication.addEventListener('change', toggleOtherField);
    toggleOtherField(); // Initial check
  }

  // ========================================
  // DIETARY RESTRICTIONS CHECKBOX HANDLER
  // ========================================
  const dietaryCheckboxes = document.querySelectorAll('.dietary-restriction-checkbox');
  const dietaryHiddenInput = document.getElementById('dietary-restrictions-hidden');

  if (dietaryCheckboxes.length > 0 && dietaryHiddenInput) {
    function updateDietaryRestrictions() {
      const selected = Array.from(dietaryCheckboxes)
        .filter(cb => cb.checked)
        .map(cb => cb.value);
      
      dietaryHiddenInput.value = selected.join(', ');
      
      // Auto-save via cart controller
      if (window.cartController) {
        window.cartController.updateCartAttributes({
          'Dietary Restrictions': dietaryHiddenInput.value
        });
      }
    }

    dietaryCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', updateDietaryRestrictions);
    });
  }

  // ========================================
  // RENTAL PERIOD SUMMARY
  // ========================================
  function updateRentalPeriodSummary() {
    const summaryElement = document.getElementById('rental-period-summary');
    if (!summaryElement) return;

    if (!window.cartController) {
      console.warn('Cart controller not available');
      return;
    }

    const startDate = window.cartController.rentalStartDate;
    const endDate = window.cartController.rentalEndDate;

    if (startDate && endDate) {
      const days = window.cartController.calculateRentalDays(startDate, endDate);
      const startFormatted = new Date(startDate).toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
      const endFormatted = new Date(endDate).toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });

      summaryElement.innerHTML = `
        <div style="display: flex; align-items: center; gap: 12px;">
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
            <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
          </svg>
          <div>
            <strong>${days} day${days !== 1 ? 's' : ''}</strong> rental
            <span style="color: var(--color-text-light); margin: 0 6px;">•</span>
            <span>${startFormatted} → ${endFormatted}</span>
          </div>
        </div>
      `;
    } else {
      summaryElement.innerHTML = `
        <div style="color: var(--color-text-light); font-style: italic;">
          No rental dates selected
        </div>
      `;
    }
  }

  function updateTightRentalNotice() {
    const notices = document.querySelectorAll('[data-tight-rental-notice]');
    const attributeInputs = document.querySelectorAll('[data-short-turnaround-attribute]');
    const startDate =
      window.cartController?.rentalStartDate ||
      notices[0]?.dataset.rentalStartDate;
    const hasTightRental = Boolean(
      startDate &&
      window.DateValidationService?.isEarliestAllowedStartDate(startDate)
    );

    notices.forEach(notice => {
      notice.hidden = !hasTightRental;
    });

    attributeInputs.forEach(input => {
      input.value = hasTightRental ? 'Yes' : '';
    });
  }

  document.getElementById('cart-form')?.addEventListener('submit', updateTightRentalNotice);

  // ========================================
  // JOB DETAILS COMPLETION CHECK
  // ========================================
  function checkJobDetailsComplete() {
    const jobDetailsSection = document.querySelector('[data-section="job-details"]');
    const completeBadge = jobDetailsSection?.querySelector('.rr-accordion__badge');
    
    if (!completeBadge || !window.cartController) return;

    const hasRentalItems = window.cartController.hasRentalItems;
    const cartItemCount = window.cartController.cart?.item_count || 0;

    // For purchase-only carts (with items), job details are always complete (no required fields)
    if (cartItemCount > 0 && !hasRentalItems) {
      completeBadge.textContent = 'Complete';
      completeBadge.classList.remove('rr-accordion__badge--incomplete');
      completeBadge.classList.add('rr-accordion__badge--complete');
      return;
    }

    // For empty carts or rental carts, check dates and data download selection
    const startDate = window.cartController.rentalStartDate;
    const endDate = window.cartController.rentalEndDate;
    const dataDownloadRadios = document.querySelectorAll('[name="attributes[Needs Data Download]"]');
    const hasDataDownloadSelection = Array.from(dataDownloadRadios).some(r => r.checked);

    if (startDate && endDate && hasDataDownloadSelection) {
      completeBadge.textContent = 'Complete';
      completeBadge.classList.remove('rr-accordion__badge--incomplete');
      completeBadge.classList.add('rr-accordion__badge--complete');
    } else {
      completeBadge.textContent = 'Required';
      completeBadge.classList.remove('rr-accordion__badge--complete');
      completeBadge.classList.add('rr-accordion__badge--incomplete');
    }
  }

  // ========================================
  // URGENT RENTAL MODAL HANDLING
  // ========================================
  function checkUrgentRental(dateValue) {
    const selectedDate = new Date(dateValue + 'T00:00:00');

    // Use the centralized business-day floor (2 PM CT cutoff) as the single source
    // of truth. Fall back to a conservative calendar offset only if the service
    // hasn't loaded.
    let earliestStart;
    if (window.DateValidationService) {
      earliestStart = new Date(window.DateValidationService.getEarliestStartDate() + 'T00:00:00');
    } else {
      earliestStart = new Date();
      earliestStart.setHours(0, 0, 0, 0);
      earliestStart.setDate(earliestStart.getDate() + 2);
    }

    if (selectedDate < earliestStart) {
      // Show Bootstrap modal
      const modalEl = document.getElementById('cart-urgent-rental-modal');
      if (modalEl && typeof bootstrap !== 'undefined') {
        const modal = new bootstrap.Modal(modalEl);
        modal.show();
      }
      return true;
    }
    return false;
  }

  // ========================================
  // INITIALIZE ON LOAD
  // ========================================
  // Wait for cart controller to be ready
  const checkCartController = setInterval(() => {
    if (window.cartController) {
      clearInterval(checkCartController);
      updateRentalPeriodSummary();
      updateTightRentalNotice();
      checkJobDetailsComplete();
    }
  }, 100);

  // Listen for cart updates
  document.addEventListener('cart:updated', () => {
    updateRentalPeriodSummary();
    updateTightRentalNotice();
    checkJobDetailsComplete();
  });

  // Listen for rental dates changed
  document.addEventListener('rentalDatesChanged', () => {
    setTimeout(() => {
      updateRentalPeriodSummary();
      updateTightRentalNotice();
      checkJobDetailsComplete();
    }, 100);
  });

  // Listen for rental status changed (via EventBus)
  if (window.eventBus) {
    window.eventBus.on('CART_RENTAL_STATUS_CHANGED', ({ hasRentalItems }) => {
      console.log('🛒 Cart rental status changed:', hasRentalItems);
      checkJobDetailsComplete();
      // Note: Page will typically reload when items are added/removed, 
      // but this ensures UI updates if status changes without reload
    });
  }

  // ========================================
  // QUANTITY CONTROLS - AUTO UPDATE
  // ========================================
  const qtyButtons = document.querySelectorAll('.rr-qty-btn');
  
  qtyButtons.forEach(button => {
    button.addEventListener('click', async (e) => {
      e.preventDefault();
      
      const action = button.dataset.action;
      const itemKey = button.dataset.key;
      const input = document.getElementById(`updates_${itemKey}`);
      
      if (!input) return;
      
      let currentQty = parseInt(input.value) || 1;
      let newQty = action === 'plus' ? currentQty + 1 : currentQty - 1;
      
      // Don't go below 0 (0 removes item)
      if (newQty < 0) newQty = 0;
      
      // Update input value
      input.value = newQty;
      
      // Show loading state on the cart item
      const cartItem = button.closest('.rr-cart-item');
      if (cartItem) {
        cartItem.classList.add('rr-updating');
      }
      
      try {
        // Update cart via Shopify API
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: itemKey,
            quantity: newQty
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to update cart');
        }
        
        // Reload page to show updated cart
        window.location.reload();
      } catch (error) {
        console.error('Error updating cart:', error);
        alert('Failed to update cart. Please try again.');
        
        // Remove loading state
        if (cartItem) {
          cartItem.classList.remove('rr-updating');
        }
        
        // Restore previous value
        input.value = currentQty;
      }
    });
  });
});
